import { supabase } from '@/lib/supabase';
import { RPC } from '@/lib/rpc';

export interface BaseNotification {
  id: string;
  type: 'comment' | 'status_change' | string;
  created_at: string;
  is_read: boolean;
}

export interface CommentNotification extends BaseNotification {
  type: 'comment';
  content_id: string;
  content_title: string;
  comment_id: string;
  comment_text: string;
  commenter_name: string;
  commenter_id: string;
  project_id: string;
  project_name: string;
}

export interface StatusChangeNotification extends BaseNotification {
  type: 'status_change';
  content_id: string;
  content_title: string;
  project_id: string;
  project_name: string;
  old_status: string;
  new_status: string;
  old_status_label: string;
  new_status_label: string;
  changed_by: string;
}

export interface InvoiceNotification extends BaseNotification {
  type: 'invoice';
  invoice_id: string;
  invoice_number: string;
  status: string;
  amount: number;
  project_name: string;
  sender_name?: string;
}

export interface ProviderPendingNotification extends BaseNotification {
  type: 'provider_pending_validation';
  provider_id: string;
  provider_name: string;
  provider_email: string;
  siret: string;
}

export interface InvoiceUploadedNotification extends BaseNotification {
  type: 'invoice_uploaded';
  invoice_id: string;
  invoice_number: string;
  amount: number;
  provider_id: string;
  provider_name: string;
  is_linked: boolean;
  project_name?: string;
}

export type Notification =
  | CommentNotification
  | StatusChangeNotification
  | InvoiceNotification
  | ProviderPendingNotification
  | InvoiceUploadedNotification;

/**
 * Get unread notifications for the current user from notifications table
 */
/** Ligne brute de la table `notifications` (avant transformation typée). */
interface RawNotification {
  id: string;
  type: string;
  reference_id: string;
  created_at: string;
  is_read: boolean;
  // `context` est un blob JSON dont les champs varient selon le type de notif.
  context?: Record<string, string | number | boolean | null | undefined>;
}

export async function getUnreadNotifications(): Promise<Notification[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  // Query notifications table for all notification types
  const { data: notifications, error } = await supabase
    .from('notifications')
    .select(`
      id,
      type,
      reference_id,
      context,
      is_read,
      created_at
    `)
    .eq('user_id', user.id)
    .eq('is_read', false)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) {
    console.error('Error fetching notifications:', error);
    return [];
  }

  if (!notifications || notifications.length === 0) return [];

  const rows = notifications as unknown as RawNotification[];

  // Separate notifications by type
  const commentNotifications = rows.filter((n) => n.type === 'comment');
  const statusChangeNotifications = rows.filter((n) => n.type === 'status_change');
  const invoiceNotifications = rows.filter((n) => n.type === 'invoice');

  const result: Notification[] = [];

  // Process comment notifications
  if (commentNotifications.length > 0) {
    const commentIds = commentNotifications.map(n => n.reference_id);
    
    const { data: comments, error: commentsError } = await supabase
      .from('editorial_calendar_comments')
      .select('id, comment, user_id')
      .in('id', commentIds);

    if (commentsError) {
      console.error('Error fetching comment details:', commentsError);
    } else {
      // Get user IDs to fetch profiles
      const userIds = [...new Set(comments?.map(c => c.user_id) || [])];
      
      // Fetch user profiles separately
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      // Map profiles by ID
      const profilesMap = new Map(
        profiles?.map(p => [p.id, p]) || []
      );

      // Map comments by ID for quick lookup
      const commentsMap = new Map(
        comments?.map(c => [c.id, c]) || []
      );

      // Transform to notification format
      commentNotifications.forEach((notification) => {
        const comment = commentsMap.get(notification.reference_id);
        if (!comment) return;

        const context = notification.context || {};
        const profile = profilesMap.get(comment.user_id);
        const commenterName = profile
          ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Utilisateur inconnu'
          : 'Utilisateur inconnu';
        
        result.push({
          id: notification.id,
          type: 'comment',
          content_id: context.content_id || '',
          content_title: context.content_title || 'Sans titre',
          comment_id: notification.reference_id,
          comment_text: comment.comment,
          commenter_name: commenterName,
          commenter_id: comment.user_id,
          project_id: context.project_id || '',
          project_name: context.project_name || 'Projet inconnu',
          created_at: notification.created_at,
          is_read: notification.is_read,
        } as CommentNotification);
      });
    }
  }

  // Process status change notifications
  statusChangeNotifications.forEach((notification) => {
    const context = notification.context || {};
    
    result.push({
      id: notification.id,
      type: 'status_change',
      content_id: context.content_id || '',
      content_title: context.content_title || 'Sans titre',
      project_id: context.project_id || '',
      project_name: context.project_name || 'Projet inconnu',
      old_status: context.old_status || '',
      new_status: context.new_status || '',
      old_status_label: context.old_status_label || '',
      new_status_label: context.new_status_label || '',
      changed_by: context.changed_by || '',
      created_at: notification.created_at,
      is_read: notification.is_read,
    } as StatusChangeNotification);
  });

  // Process invoice notifications
  invoiceNotifications.forEach((notification) => {
    const context = notification.context || {};
    
    result.push({
      id: notification.id,
      type: 'invoice',
      invoice_id: context.invoice_id || notification.reference_id || '',
      invoice_number: context.invoice_number || 'Numéro inconnu',
      status: context.status || 'unknown',
      amount: context.amount || 0,
      project_name: context.project_name || '',
      sender_name: context.sender_name,
      created_at: notification.created_at,
      is_read: notification.is_read,
    } as InvoiceNotification);
  });

  // Process provider_pending_validation notifications
  rows
    .filter((n) => n.type === 'provider_pending_validation')
    .forEach((notification) => {
      const context = notification.context || {};
      result.push({
        id: notification.id,
        type: 'provider_pending_validation',
        provider_id: notification.reference_id || context.provider_id || '',
        provider_name: context.provider_name || 'Prestataire',
        provider_email: context.provider_email || '',
        siret: context.siret || '',
        created_at: notification.created_at,
        is_read: notification.is_read,
      } as ProviderPendingNotification);
    });

  // Process invoice_uploaded notifications
  rows
    .filter((n) => n.type === 'invoice_uploaded')
    .forEach((notification) => {
      const context = notification.context || {};
      result.push({
        id: notification.id,
        type: 'invoice_uploaded',
        invoice_id: notification.reference_id || context.invoice_id || '',
        invoice_number: context.invoice_number || '',
        amount: Number(context.amount) || 0,
        provider_id: context.provider_id || '',
        provider_name: context.provider_name || 'Prestataire',
        is_linked: Boolean(context.project_name),
        project_name: context.project_name,
        created_at: notification.created_at,
        is_read: notification.is_read,
      } as InvoiceUploadedNotification);
    });

  // Sort by creation date (most recent first)
  result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return result;
}

/**
 * Get count of unread notifications
 */
export async function getUnreadNotificationCount(): Promise<number> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 0;

  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', user.id)
    .eq('is_read', false);

  if (error) {
    console.error('Error fetching notification count:', error);
    return 0;
  }

  return count || 0;
}

/**
 * Mark notification as read
 */
export async function markNotificationAsRead(notificationId: string): Promise<void> {
  const { error } = await supabase.rpc(RPC.MARK_NOTIFICATION_READ, {
    notification_id: notificationId
  });

  if (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsAsRead(): Promise<void> {
  const { error } = await supabase.rpc(RPC.MARK_ALL_NOTIFICATIONS_READ);

  if (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
}

async function notifyAllAdmins(
  type: 'provider_pending_validation' | 'invoice_uploaded',
  referenceId: string,
  context: Record<string, unknown>
): Promise<void> {
  const { error } = await supabase.rpc(RPC.INSERT_ADMIN_NOTIFICATIONS, {
    p_type: type,
    p_reference_id: referenceId,
    p_context: context,
  });
  if (error) console.error('insert_admin_notifications failed:', error);
}

async function sendDiscordNotification(
  type: string,
  fields: { name: string; value: string; inline?: boolean }[],
  context: Record<string, unknown> = {}
): Promise<void> {
  try {
    await supabase.functions.invoke('discord-notify', {
      body: { type, fields, context },
    });
  } catch (err) {
    console.error('discord-notify invoke failed:', err);
  }
}

export async function notifyProviderPendingValidation(provider: {
  id: string;
  name: string;
  email: string;
  siret: string;
}): Promise<void> {
  const context = {
    provider_id: provider.id,
    provider_name: provider.name,
    provider_email: provider.email,
    siret: provider.siret,
  };
  await Promise.all([
    notifyAllAdmins('provider_pending_validation', provider.id, context),
    sendDiscordNotification('provider_pending_validation', [
      { name: 'Nom', value: provider.name, inline: true },
      { name: 'Email', value: provider.email, inline: true },
      { name: 'SIRET', value: provider.siret || '—', inline: false },
    ], context),
  ]);
}

export async function notifyInvoiceUploaded(invoice: {
  id: string;
  invoice_number: string;
  amount: number;
  provider_id: string;
  provider_name: string;
  project_name?: string;
}): Promise<void> {
  const context = {
    invoice_id: invoice.id,
    invoice_number: invoice.invoice_number,
    amount: invoice.amount,
    provider_id: invoice.provider_id,
    provider_name: invoice.provider_name,
    project_name: invoice.project_name,
  };
  const amountStr = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(invoice.amount);
  await Promise.all([
    notifyAllAdmins('invoice_uploaded', invoice.id, context),
    sendDiscordNotification('invoice_uploaded', [
      { name: 'Prestataire', value: invoice.provider_name, inline: true },
      { name: 'Montant', value: amountStr, inline: true },
      { name: 'Projet', value: invoice.project_name ?? '— (non rattachée)', inline: false },
      { name: 'Numéro', value: invoice.invoice_number, inline: false },
    ], context),
  ]);
}